import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTexts } from '../../../hooks/texts.hook';
import ValidationSummary from '../../Shared/ValidationSummary';
import ValidationInfo from '../../../validation/validation-info';
import AntiforgeryService from '../../../services/anti-forgery.service';
import Alert from '../../Shared/Alert';
import { AlertType } from '../../Shared/Alert/models/alert.model';
import useFetch, { FetchConfig, Status } from '../../../hooks/fetch.hook';
import { SanitizerService } from '../../../services/sanitizer.service';
import IContactInformation, { emptyContactInformation } from '../../../models/contact-information.model';
import { LanguageService } from '../../../services/language.service';
import PersonalIdentityNumberLookup from '../../Shared/PersonalIdentityNumberLookup';
import { PersonalIdentityNumberFeature } from '../../Shared/PersonalIdentityNumberLookup/personal-identity-lookup-feature.enum';
import TextFieldFormGroup from '../../Shared/TextFieldFormGroup';
import IConsentTemplate, { IConsentTemplateTranslation } from '../models/consent-template.model';

import Spinner from '../../Shared/Spinner';
import ConsentTemplateApiModel from '../models/consent-template.api-model';
import { isOlderThan13 } from './create-account.helper';

export interface ICreateAccountTexts {
  consentHtml: string;
}

export type ICreateAccountProps = { texts: ICreateAccountTexts };

interface ILoginStatusResponse {
  isLoggedIn: boolean;
}

function processReceivedData(data: ConsentTemplateApiModel): IConsentTemplate {
  return ConsentTemplateApiModel.mapToBusinessModel(data);
}

function CreateAccount({ texts }: ICreateAccountProps) {
  const { t } = useTexts();

  // === STATE ===
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [{ status: currentUserIsLoggedInStatus, data: userIsLoggedInData }] = useFetch<ILoginStatusResponse>(
    '/api/mittkonto/privatecustomers/current/isauthenticated',
    false,
    false,
    FetchConfig.default,
    [retryAttempt],
  );
  const [{ status: currentIntegritypolicyStatus, data: integrityPolicyConsentData }] = useFetch<IConsentTemplate>(
    '/api/mittkonto/consenttemplates/integritypolicy',
    currentUserIsLoggedInStatus !== Status.success || userIsLoggedInData?.isLoggedIn !== false,
  );

  const [emailState, setEmailState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [personalIdentityNumberLookupState, setPersonalIdentityNumberLookupState] = useState<{
    value: IContactInformation;
    validationInfo: ValidationInfo;
  }>({ value: emptyContactInformation, validationInfo: new ValidationInfo() });
  const [personalIdValidationInfo, setPersonalIdValidationInfo] = useState(new ValidationInfo());
  const [createAccountStatus, setCreateAccountStatus] = useState<Status>(Status.notperformed);
  const [createAccountFailed, setCreateAccountFailed] = useState(false);

  // === REFS ===
  const createAccountErrorRef = useRef<HTMLDivElement>(null);

  // === VARIABLES ===
  const offerType = new URLSearchParams(window.location.search).get('erbjudande');
  const validationSummaryErrors = [...emailState.validationInfo.getErrorsWithPrefix(), ...personalIdValidationInfo.getErrorsWithPrefix()];
  const loadingDataInProgress =
    currentUserIsLoggedInStatus === Status.notperformed ||
    currentUserIsLoggedInStatus === Status.loading ||
    currentIntegritypolicyStatus === Status.loading ||
    (currentUserIsLoggedInStatus === Status.success && userIsLoggedInData?.isLoggedIn === false && currentIntegritypolicyStatus === Status.notperformed);
  const integrityPolicyConsent = integrityPolicyConsentData ? processReceivedData(integrityPolicyConsentData) : undefined;
  const initialDataFailed = currentUserIsLoggedInStatus === Status.failed || currentIntegritypolicyStatus === Status.failed;
  const canCreateAccount = currentUserIsLoggedInStatus === Status.success && userIsLoggedInData?.isLoggedIn === false && currentIntegritypolicyStatus === Status.success && !!integrityPolicyConsent;

  // === HANDLERS ===
  function validateForm(): boolean {
    const emailErrors: string[] = [];
    if (!emailState.value) emailErrors.push(t('E-postadress är obligatoriskt.', 'Email address is required.'));
    else if (emailState.value.length > 254) emailErrors.push(t('E-postadressen får inte vara längre än 254 tecken.', 'The email address cannot be longer than 254 characters.'));
    else if (!isValidEmail(emailState.value))
      emailErrors.push(t('Kontrollera att e-postadressen är i formatet: namn@exempel.com.', 'Please check that the email address is in the format: name@example.com.'));

    const personalIdErrors: string[] = [];
    if (personalIdentityNumberLookupState.value.valid && !isOlderThan13(personalIdentityNumberLookupState.value.personalIdentityNumber))
      personalIdErrors.push(t('Du måste vara över 13 år för att kunna skapa ett konto.', 'You must be over 13 years old to create an account.'));

    flushSync(() => {
      setEmailState((previousState) => ({ ...previousState, validationInfo: new ValidationInfo(t('E-post', 'Email') + ': ', emailErrors) }));
      setPersonalIdValidationInfo(new ValidationInfo(t('Personnummer', 'Personal identity number') + ': ', personalIdErrors));
    });

    if (emailErrors.length > 0 || personalIdErrors.length > 0) {
      document.getElementById('create-account-validation-summary')?.focus();
      return false;
    }
    return true;
  }

  async function handleCreateAccountClick() {
    if (!canCreateAccount || !validateForm()) return;
    setCreateAccountFailed(false);
    setCreateAccountStatus(Status.loading);

    try {
      const response = await fetch('/api/mittkonto/privatecustomers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': await AntiforgeryService.getToken(),
        },
        body: JSON.stringify({ personalIdentityNumber: personalIdentityNumberLookupState.value.personalIdentityNumber, email: emailState.value }),
      });
      if (response.ok) {
        const { passwordResetUrl } = (await response.json()) as { passwordResetUrl: string };
        window.location.href = passwordResetUrl;
      } else {
        setCreateAccountStatus(Status.failed);
        setCreateAccountFailed(true);
        createAccountErrorRef.current?.focus();
      }
    } catch {
      setCreateAccountStatus(Status.failed);
      setCreateAccountFailed(true);
    }
  }

  function t1(func: (translation: IConsentTemplateTranslation) => string): string {
    if (!integrityPolicyConsent) return '';
    if (LanguageService.getCurrentLanguage() === 'en') return func(integrityPolicyConsent.englishTranslations);
    return func(integrityPolicyConsent.swedishTranslations);
  }

  return (
    <div className="page-create-account">
      {loadingDataInProgress && <Spinner className="my-4 my-md-5" delayInMs={0} />}
      {!loadingDataInProgress && initialDataFailed && (
        <div>
          <Alert
            show={true}
            heading={t('Det gick inte att hämta uppgifterna som krävs för att skapa konto.', 'Failed to retrieve the information required to create an account.')}
            headingTag="div"
            type={AlertType.Error}
            closable={false}
            className="mb-3"
          />
          <button type="button" className="btn btn-secondary" onClick={() => setRetryAttempt((previousAttempt) => previousAttempt + 1)}>
            {t('Försök igen', 'Try again')}
          </button>
        </div>
      )}
      <Alert
        show={!loadingDataInProgress && currentUserIsLoggedInStatus === Status.success && userIsLoggedInData?.isLoggedIn === true}
        heading={t('Du är redan inloggad.', 'You are already logged in.')}
        headingTag="div"
        type={AlertType.Default}
        closable={false}
      />
      {!loadingDataInProgress && canCreateAccount && (
        <div>
          {offerType === 'senior' && (
            <div className="border border-vtd-gray-700 rounded p-3 mb-4">
              <h2>{t('Beställ Västtrafiks seniorbiljett', 'Order Västtrafik’s senior ticket')}</h2>
              <p>{t('Här beställer du din seniorbiljett. Skapa ett konto för att komma till erbjudandet.', 'Here you can order your senior ticket. Create an account to access the offer.')}</p>
              <a href="https://www.vasttrafik.se/biljetter/andra-biljetter/seniorkort/">{t('Mer information om seniorbiljetten', 'More information about the senior ticket')}</a>
            </div>
          )}

          {validationSummaryErrors.length > 0 && <h2 className="visually-hidden">{t('Felmeddelanden', 'Error messages')}</h2>}
          <ValidationSummary id="create-account-validation-summary" validationSummaryErrors={validationSummaryErrors} className="mb-4" />
          <div tabIndex={-1} ref={createAccountErrorRef}>
            <Alert
              show={createAccountFailed}
              heading={t('Det gick inte att skapa ett konto', 'Failed to create an account')}
              content={`<p>${t('Det kan bero på att det redan finns ett konto med den e-postadress eller det personnummer som du angav.', 'This may be because an account already exists with the email address or personal identity number you entered.')}</p><p>${t('Om du redan har ett konto men inte lyckats logga in kan du få hjälp av kundservice.', 'If you already have an account but have not been able to log in, you can get help from customer service.')}</p>`}
              headingTag="div"
              type={AlertType.Error}
              closable={false}
              className="mb-3"
            />
          </div>
          <TextFieldFormGroup
            inputId="email"
            labelText={t('E-post', 'Email')}
            required={true}
            type="email"
            autocomplete="email"
            className="mb-3"
            state={emailState}
            onChange={(value) => setEmailState((previousState) => ({ ...previousState, value, validationInfo: new ValidationInfo() }))}
          />
          <PersonalIdentityNumberLookup
            state={personalIdentityNumberLookupState}
            feature={PersonalIdentityNumberFeature.CreatePrivateCustomerAccount}
            noPersonalIdentityNumberText=""
            lookupButtonText={t('Hämta personuppgifter', 'Get personal data')}
            onChange={(value) => {
              setPersonalIdentityNumberLookupState((previousState) => ({ ...previousState, value }));
              setPersonalIdValidationInfo(new ValidationInfo());
            }}
          />
          {personalIdValidationInfo.isInvalid && <div className="invalid-feedback d-block mb-3">{personalIdValidationInfo.errors[0]}</div>}
          <div className="mb-3" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(texts.consentHtml) }} />
          <div className="mb-3">
            {t1((x) => x.text)} <a href={t1((x) => x.url)}>{t('Västtrafiks integritetspolicy', 'Västtrafiks privacy policy')}</a>.
          </div>
          {personalIdentityNumberLookupState.value.valid && (
            <button type="button" className="btn btn-primary w-100" disabled={createAccountStatus === Status.loading} onClick={handleCreateAccountClick}>
              {createAccountStatus === Status.loading ? t('Skapar konto', 'Creating account') : t('Fortsätt', 'Continue')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function isValidEmail(email: string): boolean {
  const an = 'a-zA-Z0-9';
  return new RegExp(`^[${an}][${an}_.+-]*[${an}_+-]@([${an}-]+\\.)*[${an}-]+(\\.[${an}-]+)+$|^[${an}]@([${an}-]+\\.)*[${an}-]+(\\.[${an}-]+)+$`).test(email);
}

export default CreateAccount;
